import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {makeStyles} from '@material-ui/styles';
import {
    Card,
    CardHeader,
    CardContent,
    Divider,
    Typography
} from '@material-ui/core';
import {currencyKr} from 'helpers/currencyKr';

const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%'
    },
    commentContainer: {
        [theme.breakpoints.down('sm')]: {
            columnCount: 1
        },
        position: 'relative',
        columnGap: '2em',
        columnCount: 2,
        columnRuleColor: '#eeeeee',
        columnRuleStyle: 'solid',
        columnRuleWidth: 'thin',
        wordBreak: 'keep-all'
    }
}));

const VARIANCE = {
    'up': '상승',
    'down': '하락',
    'middle': '동일'
};

const VARIANCE_SENTENCE = {
    'up': '단계 상승',
    'down': '단계 하락',
    'middle': '동일 순위 유지'
};

const TodayComment = props => {
    const {className, data, ...rest} = props;
    const classes = useStyles(props);
    const {
        date,
        category,
        point,
        rate,
        variance,
        compare,
        maker = [],
        platform = [],
        seller = []
    } = data;

    const getStatus = useCallback(({change}) => {
        const {ranking, status} = change;
        const diff = VARIANCE_SENTENCE[status];
        const step = Math.abs(ranking);

        return ranking === 'new'
            ? '새로 순위에 진입'
            : `${step && status !== 'middle' ? step : ''}${diff}`;
    }, []);

    const getVariance = status => VARIANCE[status];

    // TODO : 데이터 없을 경우 예외 처리

    const concatItemName = list => list.reduce((comment, item) => `${comment}, ${item.name}`, '').slice(2)

    const getDetailComment = useCallback((list) => {
        const first = list.length ? list.shift() : undefined;
        const comment1 = first ? ` ${first.name}의 순위는 ${compare} 대비 ${getStatus(first)} 하였음.` : '';
        const comment2 = list.length ? ` ${concatItemName(list)}은 동일 기간 각각 ${list.reduce((comment, item) => `${comment}, ${getStatus(item)}`, '').slice(2)} 하였음.` : '';

        return comment1 + comment2;
    }, [compare, getStatus]);

    const makerComment = useMemo(() => {
        const comment1 = `${date} ${category} 매출은 ${currencyKr(point)}으로 ${compare} 대비 ${rate}% ${getVariance(variance)} 하였음.`;
        const comment2 = maker.length ? ` ${category}에서 가장 높은 점수를 얻은 상위 ${maker.length}개 제조사는 ${concatItemName(maker)} 임.` : '';
        const comment3 = getDetailComment(maker);

        return comment1 + comment2 + comment3;
    }, [date, category, point, compare, rate, variance, maker, getDetailComment]);

    const platformComment = useMemo(() => {
        const comment1 = platform.length ? `플랫폼 기준으로는 ${concatItemName(platform)}이 가장 좋은 성과를 거두었음.` : '';
        const comment2 = getDetailComment(platform);

        return comment1 + comment2;
    }, [platform, getDetailComment]);

    const sellerComment = useMemo(() => {
        const comment1 = seller.length ? `셀러 기준으로는 ${concatItemName(seller)}이 가장 좋은 성과를 거두었음.` : '';
        const comment2 = getDetailComment(seller);

        return comment1 + comment2;
    }, [seller, getDetailComment]);

    return (
        <Card
            {...rest}
            className={clsx(classes.root, className)}
        >
            <CardHeader
                title="Today's Comment"
            />
            <Divider/>
            <CardContent>
                <div className={classes.commentContainer}>
                    <Typography paragraph>
                        {makerComment}
                    </Typography>
                    <Typography paragraph>
                        {platformComment}
                    </Typography>
                    <Typography paragraph>
                        {sellerComment}
                    </Typography>
                </div>
            </CardContent>
        </Card>
    );
};

TodayComment.propTypes = {
    className: PropTypes.string,
    data: PropTypes.object.isRequired
};

export default TodayComment;
